// Reads the published content release that `pnpm fetch:content` baked into
// this site's own bucket before the build.
//
// The files are served same-origin from /site-content, in the same layout the
// platform publishes them in: a channel pointer, an immutable revision
// directory, a manifest, and one shard per collection. Nothing here talks to
// the platform at runtime — a visitor's browser only ever reads this site.
//
// Every collection carries a state, and it has three values for a reason:
//
//   populated       there are records; render the section
//   empty           this institution uses the module and has published nothing
//                   yet; render the section's empty state
//   not_configured  this institution does not use the module at all; the
//                   section should not exist on the site
//
// Collapsing the last two into "no records" is what produces a site full of
// empty shells for modules an institution never intends to use.

import { useEffect, useState } from 'react';
import {
  COLLECTION_NAMES,
  loadContent,
  type CollectionName,
  type CollectionState,
  type Collections,
  type SiteContentSnapshot
} from '@zivin-labs/site-content';

// Inlined at build time by vite.config.ts from the same resolver the content
// fetch used (site.config.mjs), so these always name the release on disk.
declare const __SITE_CODE__: string;
declare const __SITE_LOCALE__: string;

const SITE_CODE = __SITE_CODE__;
const LOCALE = __SITE_LOCALE__;
const BASE_URL = '/site-content';

// When no release has been fetched, every collection reads as not_configured so
// sections hide instead of rendering empty shells. Reported through `absent` so
// a caller can tell "nothing published" from "nothing fetched".
const ALL_NOT_CONFIGURED = Object.fromEntries(
  COLLECTION_NAMES.map((name) => [name, 'not_configured' as CollectionState])
) as Record<CollectionName, CollectionState>;

const EMPTY_COLLECTIONS = Object.fromEntries(
  COLLECTION_NAMES.map((name) => [name, []])
) as unknown as Collections;

export interface SiteContentState {
  snapshot: SiteContentSnapshot | null;
  states: Record<CollectionName, CollectionState>;
  loading: boolean;
  /** No release could be read at all — distinct from a release with no records. */
  absent: boolean;
  error: Error | null;
}

// One in-flight load shared by every consumer, so a page mounting six sections
// costs one manifest read rather than six.
let cache: Promise<SiteContentSnapshot> | null = null;
function load(): Promise<SiteContentSnapshot> {
  if (!cache) {
    cache = loadContent({
      tenantId: SITE_CODE,
      locale: LOCALE,
      baseUrl: BASE_URL
    });
  }
  return cache;
}

export function useSiteContent(): SiteContentState {
  const [state, setState] = useState<SiteContentState>({
    snapshot: null,
    states: ALL_NOT_CONFIGURED,
    loading: true,
    absent: false,
    error: null
  });

  useEffect(() => {
    let alive = true;
    load()
      .then((snapshot) => {
        if (!alive) return;
        setState({
          snapshot,
          states: snapshot.states,
          loading: false,
          absent: false,
          error: null
        });
      })
      .catch((err: Error) => {
        if (!alive) return;
        cache = null; // let the next mount retry
        setState({
          snapshot: null,
          states: ALL_NOT_CONFIGURED,
          loading: false,
          absent: true,
          error: err
        });
      });
    return () => {
      alive = false;
    };
  }, []);

  return state;
}

export interface CollectionResult<N extends CollectionName> {
  items: Collections[N];
  state: CollectionState;
  loading: boolean;
  /** True when the section should not be rendered at all. */
  hidden: boolean;
}

/**
 * One collection, with the judgement a section needs already made: `hidden` is
 * true when this institution does not use the module, which is the only case
 * where the right answer is to render nothing rather than an empty state.
 */
export function useCollection<N extends CollectionName>(
  name: N
): CollectionResult<N> {
  const { snapshot, states, loading } = useSiteContent();
  const state = states[name];
  return {
    items: (snapshot?.collections[name] ?? EMPTY_COLLECTIONS[name]) as Collections[N],
    state,
    loading,
    hidden: !loading && state === 'not_configured'
  };
}

/** The site chrome: name, logo, contact, menu, footer, announcements. */
export function useSiteChrome() {
  const { snapshot, loading, absent } = useSiteContent();
  return { site: snapshot?.site ?? null, loading, absent };
}
